import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  ArrowUpRight,
  ArrowRight,
  Menu,
  X,
  Compass,
  Building2,
  Phone,
  LogIn,
  Star,
} from 'lucide-react'
import api from '../../services/api'

function toKampalaDateStr(value) {
  if (value == null || value === '') return null
  const str = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
  const parsed = new Date(str)
  if (Number.isNaN(parsed.getTime())) return null
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Africa/Kampala',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(parsed)
  } catch {
    return str.slice(0, 10)
  }
}

/** Close of registration = 23:59:59 Africa/Kampala on that calendar day. */
function kampalaCloseDeadline(value) {
  const dateStr = toKampalaDateStr(value)
  if (!dateStr) return null
  const [year, month, day] = dateStr.split('-').map(Number)
  if (!year || !month || !day) return null
  return Date.UTC(year, month - 1, day, 20, 59, 59)
}

function remainingUntil(deadlineMs) {
  const totalMs = deadlineMs - Date.now()
  if (totalMs <= 0) {
    return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 }
  }
  const totalSec = Math.floor(totalMs / 1000)
  return {
    expired: false,
    days: Math.floor(totalSec / 86400),
    hours: Math.floor((totalSec % 86400) / 3600),
    minutes: Math.floor((totalSec % 3600) / 60),
    seconds: totalSec % 60,
  }
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function formatCompactCountdown(remaining) {
  if (!remaining || remaining.expired) return 'Registration closed'
  const { days, hours, minutes, seconds } = remaining
  if (days > 0) return `Closes in ${days}d ${pad2(hours)}h ${pad2(minutes)}m`
  if (hours > 0) return `Closes in ${pad2(hours)}h ${pad2(minutes)}m ${pad2(seconds)}s`
  return `Closes in ${pad2(minutes)}m ${pad2(seconds)}s`
}

function usePrimaryIntakeCountdown() {
  const [intake, setIntake] = useState(null)
  const [registrationOpen, setRegistrationOpen] = useState(false)
  const [remaining, setRemaining] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function loadIntake() {
      try {
        const openRes = await api.get('/intakes/open')
        if (cancelled) return
        const openList = Array.isArray(openRes.data?.intakes) ? openRes.data.intakes : []
        const flagged = openList.find((row) => row.is_primary_registration_intake)
        const openIntake = flagged || openList[0] || null

        if (openIntake) {
          setIntake(openIntake)
          setRegistrationOpen(true)
          setFailed(false)
          return
        }

        const primaryRes = await api.get('/intakes/primary')
        if (cancelled) return
        setIntake(primaryRes.data?.primary_intake || null)
        setRegistrationOpen(primaryRes.data?.registration_open === true)
        setFailed(false)
      } catch {
        if (!cancelled) {
          setIntake(null)
          setRegistrationOpen(false)
          setFailed(true)
        }
      } finally {
        if (!cancelled) setLoaded(true)
      }
    }

    loadIntake()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const deadline = kampalaCloseDeadline(intake?.registration_close_date)
    if (deadline == null) {
      setRemaining(null)
      return undefined
    }
    const tick = () => setRemaining(remainingUntil(deadline))
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [intake?.registration_close_date])

  const label = !loaded || failed
    ? null
    : !registrationOpen
      ? 'Registration closed'
      : remaining
        ? formatCompactCountdown(remaining)
        : 'Applications open'

  return { intake, remaining, loaded, isOpen: registrationOpen && !(remaining?.expired), label }
}

function CountdownBlocks({ remaining, compact = false }) {
  if (!remaining || remaining.expired) return null
  const units = [
    ['Days', remaining.days],
    ['Hrs', remaining.hours],
    ['Min', remaining.minutes],
    ['Sec', remaining.seconds],
  ]
  return (
    <div className={`flex ${compact ? 'gap-1' : 'mt-3 gap-1.5 xl:gap-2'}`}>
      {units.map(([unitLabel, value]) => (
        <div
          key={unitLabel}
          className={`text-center border border-white/15 bg-black/55 ${
            compact ? 'min-w-[2.4rem] px-1.5 py-1' : 'min-w-[3.1rem] xl:min-w-[3.5rem] px-2 py-1.5'
          }`}
        >
          <p
            className={`font-display font-bold text-[#e5a93c] tabular-nums leading-none ${
              compact ? 'text-sm' : 'text-base xl:text-lg'
            }`}
          >
            {pad2(value)}
          </p>
          <p
            className={`uppercase tracking-[0.16em] text-white/60 font-medium ${
              compact ? 'mt-0.5 text-xs' : 'mt-1 text-xs'
            }`}
          >
            {unitLabel}
          </p>
        </div>
      ))}
    </div>
  )
}

const PATH_CARDS = [
  {
    id: 'advisor',
    num: '01',
    to: '/advisor',
    title: 'Talk to an Admissions Advisor',
    shortTitle: 'Admissions Advisor',
    subtitle: 'Personal Career Guidance',
    description: 'Connect with our admissions team to find the exact program for your career goals.',
    image: '/Tutors/Robert.jpg',
    tag: 'ADMISSIONS ADVISOR',
    position: 'object-[center_8%]', // Perfectly frames Robert's head & face
  },
  {
    id: 'explore',
    num: '02',
    to: '/explore',
    title: 'Explore SMA',
    shortTitle: 'Explore SMA',
    subtitle: 'Campus, Programs & Creative Studios',
    description: 'Discover our cutting-edge audio, film, design & photography facilities.',
    image: '/Gallery/Class rooms/20250822_165220.jpg',
    tag: 'EXPLORE ACADEMY',
    position: 'object-center',
  },
  {
    id: 'masterclass',
    num: '03',
    to: '/masterclass',
    title: 'The SMA Masterclass',
    shortTitle: 'Masterclass',
    subtitle: 'Monthly Live Industry Sessions',
    description: 'Learn directly from top African & international creative leaders and directors.',
    image: '/Gallery/Academy/Learning In Action/971.jpg',
    tag: 'LIVE MASTERCLASS',
    position: 'object-center',
  },
  {
    id: 'register',
    num: '04',
    to: '/register',
    title: 'I Already Know — Register',
    shortTitle: 'Apply now',
    subtitle: 'Direct Intake Application',
    description: 'Ready to join? Submit your application directly and secure your intake spot.',
    image: '/Gallery/Academy/academy.jpg',
    tag: 'APPLY NOW',
    position: 'object-[center_20%]', // Frames student and screen
  },
]

export default function WelcomeExperience() {
  const navigate = useNavigate()
  const [ready, setReady] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [activePath, setActivePath] = useState(0) // Default active item index
  const { intake, remaining, label: countdownLabel, isOpen: registrationIsOpen } = usePrimaryIntakeCountdown()

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 60)
    return () => clearTimeout(t)
  }, [])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  const choose = (to) => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion) {
      navigate(to)
      return
    }
    setLeaving(true)
    window.setTimeout(() => navigate(to), 350)
  }

  const currentItem = PATH_CARDS[activePath] || PATH_CARDS[0]

  return (
    <div
      className={`welcome-experience h-[100dvh] max-h-[100dvh] overflow-hidden bg-[#070707] text-white flex flex-col justify-between relative selection:bg-[#e5a93c]/30 selection:text-white transition-opacity duration-500 ${
        leaving ? 'opacity-0 scale-[0.99]' : 'opacity-100 scale-100'
      }`}
    >
      {/* Film grain texture */}
      <div className="cinema-grain" aria-hidden="true" />

      {/* ──────────────────────────────────────────────────────────
          DESKTOP & TABLET LAYOUT (>= 1024px) — SINGLE SCREEN 100dvh
      ────────────────────────────────────────────────────────── */}
      <div className="hidden lg:flex h-full w-full relative z-10 overflow-hidden">
        
        {/* ── LEFT PANEL: HERO, VALUE PROP & MANIFESTO ── */}
        <div className="w-[45%] xl:w-[42%] 2xl:w-[40%] h-full flex flex-col justify-between p-8 xl:p-12 2xl:p-14 border-r border-white/10 relative overflow-hidden bg-[#070707] shrink-0">
          
          {/* Geometric ambient accents in bottom right (sharp straight lines) */}
          <div className="absolute -bottom-16 -right-16 w-[280px] h-[280px] border border-[#e5a93c]/15 bg-[#e5a93c]/[0.02] pointer-events-none" />
          <div className="absolute -bottom-8 -right-8 w-[160px] h-[160px] border border-[#e5a93c]/10 pointer-events-none" />

          {/* Top Header */}
          <header
            className={`flex items-center justify-between shrink-0 transition-all duration-700 ${
              ready ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <img
                src="/Logos/SMA LOGO  DARK MODE.png"
                alt="Synergy Multimedia Academy"
                className="h-8 xl:h-9 object-contain shrink-0"
              />
              <div className="border-l border-white/15 pl-3 min-w-0">
                <p className="font-display text-sm font-semibold tracking-tight text-white leading-tight xl:hidden">
                  SMA
                </p>
                <p className="hidden xl:block font-display text-sm font-semibold tracking-tight text-white leading-tight">
                  SYNERGY MULTIMEDIA ACADEMY
                </p>
                <p className="text-xs uppercase tracking-[0.22em] text-[#e5a93c] font-medium">
                  SHAPING FUTURES
                </p>
              </div>
            </div>

            <nav className="flex items-center gap-3 xl:gap-4 text-xs tracking-wider uppercase font-sans shrink-0 ml-4">
              <Link to="/explore#story" className="text-white/60 hover:text-white transition-colors">
                Academy
              </Link>
              <Link to="/programs#programs-catalog" className="text-white/60 hover:text-white transition-colors">
                Programs
              </Link>
              <Link to="/login" className="text-[#e5a93c] hover:text-white transition-colors font-semibold">
                Login
              </Link>
            </nav>
          </header>

          {/* Hero Content */}
          <div
            className={`my-auto py-4 xl:py-6 max-w-lg transition-all duration-700 delay-150 ${
              ready ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <h1 className="font-display font-bold text-white text-3xl xl:text-4xl 2xl:text-5xl leading-[1.08] tracking-tight">
              Turn your
              <br />
              creative skill
              <br />
              into a career.
            </h1>

            <p className="mt-3.5 xl:mt-4 font-sans font-light text-white/70 text-xs xl:text-sm 2xl:text-base leading-relaxed max-w-md">
              Practical multimedia training for photographers, filmmakers, designers and the next generation of digital creators.
            </p>

            {/* CTA Button Group (Sharp straight edges) */}
            <div className="mt-6 xl:mt-7 flex items-center gap-3.5">
              <button
                type="button"
                onClick={() => choose('/programs#programs-catalog')}
                className="bg-[#e5a93c] hover:bg-[#d4a853] text-black font-sans font-bold text-xs xl:text-sm uppercase tracking-wider px-6 xl:px-7 py-3 transition-all duration-300 shadow-lg hover:shadow-[#e5a93c]/20 active:scale-95 cursor-pointer"
              >
                VIEW ALL PROGRAMS
              </button>

              <button
                type="button"
                onClick={() => choose('/advisor')}
                className="bg-white/[0.04] hover:bg-white/[0.08] text-white font-sans font-semibold text-xs xl:text-sm uppercase tracking-wider px-6 xl:px-7 py-3 border border-white/20 hover:border-white/40 transition-all duration-300 active:scale-95 cursor-pointer"
              >
                TALK TO ADVISOR
              </button>
            </div>

            {countdownLabel && (
              <p className="mt-4 text-xs xl:text-sm text-[#e5a93c] font-sans tabular-nums font-medium" aria-live="polite">
                {intake?.name ? `${intake.name} · ` : ''}
                {countdownLabel}
              </p>
            )}

            {/* Meta Trust Statement */}
            <p className="mt-5 text-white/50 text-xs xl:text-sm font-sans tracking-wide font-medium">
              3 months &bull; Hands-on &bull; Industry-focused
            </p>
          </div>

          {/* Bottom Manifesto */}
          <footer
            className={`shrink-0 transition-all duration-700 delay-300 ${
              ready ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
            }`}
          >
            <p className="font-display font-semibold text-white/90 text-lg xl:text-xl 2xl:text-2xl leading-snug tracking-tight">
              Learn the craft.
              <br />
              Build the portfolio.
              <br />
              Become the professional.
            </p>
          </footer>
        </div>

        {/* ── RIGHT PANEL: EDITORIAL INDEX & DYNAMIC PREVIEW (DESKTOP) ── */}
        <div className="w-[55%] xl:w-[58%] 2xl:w-[60%] h-full flex flex-col justify-between relative overflow-hidden bg-[#0b0b0b]">
          <div className="relative w-full h-full flex flex-col justify-between p-6 xl:p-8 2xl:p-10 z-10 overflow-hidden">
            
            {/* Top Header */}
            <div className="flex items-center justify-between shrink-0 mb-2.5 gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-1.5 h-1.5 bg-[#e5a93c]" />
                <p className="text-[#e5a93c] text-xs uppercase tracking-[0.24em] font-semibold font-sans">
                  CHOOSE YOUR PATHWAY
                </p>
              </div>
              {registrationIsOpen && remaining && !remaining.expired && (
                <div className="shrink-0" aria-label={countdownLabel || 'Registration countdown'}>
                  <CountdownBlocks remaining={remaining} compact />
                </div>
              )}
            </div>

            {/* Large Dynamic Cinematic Preview Window (~62%-65% of section height) */}
            <button
              type="button"
              onClick={() => choose(currentItem.to)}
              aria-label={currentItem.title}
              className="group relative w-full h-[60%] xl:h-[64%] 2xl:h-[66%] min-h-[280px] overflow-hidden border border-white/20 hover:border-[#e5a93c]/60 shadow-2xl transition-all duration-500 shrink-0 mb-3 bg-[#141414] text-left"
            >
              <img
                src={currentItem.image}
                alt=""
                className={`w-full h-full object-cover ${currentItem.position || 'object-center'} transition-transform duration-700 ease-out group-hover:scale-105`}
              />
              {/* Gradient Overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#090909] via-[#090909]/40 to-transparent" />
              <div className="absolute inset-0 bg-[#090909]/20" />

              {/* Overlay Details */}
              <div className="absolute bottom-0 inset-x-0 p-5 xl:p-7 flex items-end justify-between">
                <div>
                  <span className="inline-block bg-[#e5a93c] text-black text-xs font-bold uppercase tracking-wider px-2.5 py-1 mb-2">
                    {currentItem.tag}
                  </span>
                  <p className="font-display font-bold text-white text-xl xl:text-3xl leading-tight">
                    {currentItem.title}
                  </p>
                  {currentItem.id === 'register' && registrationIsOpen && remaining && !remaining.expired && (
                    <CountdownBlocks remaining={remaining} />
                  )}
                  <p className="font-sans text-white/80 text-xs xl:text-sm mt-1.5 max-w-xl line-clamp-2">
                    {currentItem.id === 'register' && intake?.name
                      ? `${currentItem.description} ${intake.name}.`
                      : currentItem.description}
                  </p>
                </div>

                <div className="w-10 h-10 xl:w-11 xl:h-11 bg-[#e5a93c] text-black flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shrink-0 ml-4">
                  <ArrowUpRight className="w-5 h-5" />
                </div>
              </div>
            </button>

            {/* 2x2 Side-by-Side Editorial Pathway Grid */}
            <div className="grid grid-cols-2 gap-2.5 xl:gap-3 flex-1">
              {PATH_CARDS.map((card, idx) => {
                const isActive = activePath === idx
                const isRegister = card.id === 'register'
                return (
                  <button
                    key={card.id}
                    type="button"
                    onMouseEnter={() => setActivePath(idx)}
                    onFocus={() => setActivePath(idx)}
                    onClick={() => choose(card.to)}
                    aria-label={card.title}
                    className={`group w-full text-left flex items-center justify-between p-3 xl:p-3.5 transition-all duration-300 ${
                      isRegister
                        ? `devin-glowing-card ${isActive ? 'is-active' : ''}`
                        : `border border-white/10 ${
                            isActive
                              ? 'bg-white/[0.08] border-l-2 border-l-[#e5a93c] shadow-lg'
                              : 'bg-[#111111]/70 hover:bg-white/[0.04]'
                          }`
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className={`font-display font-bold text-xs xl:text-sm shrink-0 ${
                          isActive || isRegister ? 'text-[#e5a93c]' : 'text-white/30 group-hover:text-white/60'
                        }`}
                      >
                        {card.num}
                      </span>
                      <div className="min-w-0">
                        <p
                          className={`font-display font-semibold text-xs xl:text-sm truncate transition-colors ${
                            isActive || isRegister ? 'text-white' : 'text-white/80 group-hover:text-white'
                          }`}
                        >
                          {card.shortTitle}
                        </p>
                        <p
                          className={`font-sans text-xs truncate tabular-nums mt-0.5 ${
                            isRegister ? 'text-[#e5a93c] font-medium' : 'text-white/50'
                          }`}
                          {...(isRegister && countdownLabel ? { 'aria-live': 'polite' } : {})}
                        >
                          {isRegister && countdownLabel ? countdownLabel : card.subtitle}
                        </p>
                      </div>
                    </div>

                    <ArrowRight
                      className={`w-4 h-4 shrink-0 ml-2 transition-all duration-300 ${
                        isActive || isRegister
                          ? 'text-[#e5a93c] translate-x-1'
                          : 'text-white/20 group-hover:text-white/60'
                      }`}
                    />
                  </button>
                )
              })}
            </div>

            {/* Bottom Copyright */}
            <div className="pt-2 border-t border-white/5 flex items-center justify-between text-white/40 text-xs font-sans shrink-0 mt-2">
              <p>&copy; {new Date().getFullYear()} Synergy Multimedia Academy</p>
              <p className="uppercase tracking-widest text-xs">Kampala &bull; Uganda</p>
            </div>
          </div>
        </div>
      </div>

      {/* ──────────────────────────────────────────────────────────
          MOBILE LAYOUT (< 1024px) — COMPACT APP HOME
      ────────────────────────────────────────────────────────── */}
      <div className="lg:hidden flex flex-col h-[100dvh] max-h-[100dvh] px-4 pt-[max(0.875rem,env(safe-area-inset-top))] pb-[max(0.875rem,env(safe-area-inset-bottom))] relative z-10 overflow-hidden bg-[#070707]">

        {/* Static cinematic still — top third only so tiles own their photos */}
        <div className="absolute inset-x-0 top-0 h-[38%] z-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <img
            src="/welcome/hero-creator.jpg"
            alt=""
            className="w-full h-full object-cover object-[center_20%] opacity-35"
          />
          <div className="absolute inset-0 bg-[#070707]/50" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/55 via-[#070707]/45 to-[#070707]" />
        </div>

        {/* Slim app bar */}
        <header
          className={`relative z-10 flex items-center justify-between shrink-0 transition-all duration-700 ${
            ready ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
          }`}
        >
          <div className="flex items-center gap-2">
            <img
              src="/Logos/SMA LOGO  DARK MODE.png"
              alt="Synergy Multimedia Academy"
              className="h-7 object-contain"
            />
            <div className="border-l border-white/15 pl-2">
              <span className="font-display text-sm font-bold tracking-tight text-white leading-none">
                SMA
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => choose('/login')}
              className="min-h-10 px-2.5 text-[#e5a93c] font-sans font-semibold text-xs uppercase tracking-[0.14em] hover:text-white transition-colors"
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="w-10 h-10 flex items-center justify-center text-[#e5a93c] border border-[#e5a93c]/40 hover:border-[#e5a93c] bg-black/30 transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Compact value prop — no duplicate CTAs */}
        <section
          className={`relative z-10 shrink-0 pt-4 pb-3 sm:pt-5 sm:pb-4 transition-all duration-700 delay-150 ${
            ready ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
          }`}
        >
          <h1 className="font-display font-bold text-white text-lg sm:text-xl leading-tight tracking-tight">
            Turn your creative skill into a career.
          </h1>
          <p className="mt-1.5 font-sans font-light text-white/80 text-xs sm:text-sm leading-relaxed max-w-sm">
            Practical studio training in Photography, Filmmaking, Design & 3D Animation.
          </p>
          <button
            type="button"
            onClick={() => choose('/programs#programs-catalog')}
            className="mt-3 w-full py-2.5 bg-[#e5a93c] hover:bg-[#d4a853] text-black font-sans font-bold text-xs sm:text-sm uppercase tracking-wider transition-transform active:scale-[0.98]"
          >
            VIEW ALL PROGRAMS
          </button>
          {countdownLabel && (
            <p className="mt-2 text-center text-xs sm:text-sm text-[#e5a93c] font-sans tabular-nums font-medium" aria-live="polite">
              {countdownLabel}
              {intake?.name ? ` · ${intake.name}` : ''}
            </p>
          )}
        </section>

        {/* 2x2 destination tiles — one tap navigates */}
        <section
          className={`relative z-10 flex-1 min-h-0 grid grid-cols-2 grid-rows-2 gap-2 sm:gap-2.5 transition-all duration-700 delay-300 ${
            ready ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {PATH_CARDS.map((card) => {
            const isRegister = card.id === 'register'
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => choose(card.to)}
                aria-label={card.title}
                className={`group relative min-h-[120px] h-full w-full overflow-hidden text-left transition-transform active:scale-[0.98] ${
                  isRegister
                    ? 'devin-glowing-card is-active'
                    : 'border border-white/15 bg-[#141414]'
                }`}
              >
                <span className={`!absolute overflow-hidden ${isRegister ? 'inset-[1px]' : 'inset-0'}`}>
                  <img
                    src={card.image}
                    alt=""
                    className={`w-full h-full object-cover ${card.position || 'object-center'} transition-transform duration-700 ease-out group-active:scale-105`}
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <span className="absolute inset-x-0 bottom-0 p-2.5 sm:p-3 flex items-end justify-between gap-2">
                    <span className="min-w-0">
                      <span className="block font-display font-bold text-xs text-[#e5a93c] leading-none mb-1">
                        {card.num}
                      </span>
                      <span className="block font-display font-semibold text-sm sm:text-base text-white leading-tight">
                        {card.shortTitle}
                      </span>
                      {isRegister && countdownLabel && (
                        <span className="mt-0.5 block font-sans text-xs text-[#e5a93c] tabular-nums leading-tight font-medium">
                          {countdownLabel}
                        </span>
                      )}
                    </span>
                    <span
                      className={`w-7 h-7 shrink-0 flex items-center justify-center ${
                        isRegister ? 'bg-[#e5a93c] text-black' : 'bg-white/15 text-white'
                      }`}
                    >
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </span>
                </span>
              </button>
            )
          })}
        </section>
      </div>

      {/* ──────────────────────────────────────────────────────────
          SLIDE-OUT MOBILE NAVIGATION DRAWER (SHARP EDGES)
      ────────────────────────────────────────────────────────── */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setMenuOpen(false)}
          />

          {/* Drawer Panel */}
          <div className="relative w-full max-w-xs sm:max-w-sm h-full bg-[#0d0d0d] border-l border-white/10 p-6 flex flex-col justify-between overflow-y-auto shadow-2xl z-10 animate-in slide-in-from-right duration-300">
            <div>
              {/* Header inside drawer */}
              <div className="flex items-center justify-between pb-5 border-b border-white/10">
                <img
                  src="/Logos/SMA LOGO  DARK MODE.png"
                  alt="Synergy Multimedia Academy"
                  className="h-7 object-contain"
                />
                <button
                  type="button"
                  onClick={() => setMenuOpen(false)}
                  className="w-8 h-8 border border-white/20 bg-white/5 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Navigation Links */}
              <nav className="mt-6 space-y-2">
                <p className="text-xs uppercase tracking-[0.25em] text-[#e5a93c] font-semibold mb-3">
                  Welcome Modes
                </p>

                {PATH_CARDS.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() => {
                      setMenuOpen(false)
                      choose(card.to)
                    }}
                    className="w-full flex items-center justify-between p-3 bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 hover:border-[#e5a93c]/50 transition-colors text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-display font-semibold text-xs text-[#e5a93c]">
                        {card.num}
                      </span>
                      <span className="font-display text-sm text-white font-medium group-hover:text-[#e5a93c] transition-colors">
                        {card.title}
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#e5a93c] group-hover:translate-x-0.5 transition-all" />
                  </button>
                ))}

                <div className="pt-4 pb-2">
                  <div className="h-px w-full bg-white/10" />
                </div>

                <p className="text-xs uppercase tracking-[0.25em] text-white/50 font-semibold mb-2">
                  Academy Quick Links
                </p>

                <Link
                  to="/explore"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 p-2.5 text-white/80 hover:text-white hover:bg-white/5 text-sm transition-colors border border-transparent hover:border-white/10"
                >
                  <Compass className="w-4 h-4 text-[#e5a93c]" />
                  <span>Explore Programs</span>
                </Link>

                <Link
                  to="/showcase"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 p-2.5 text-white/80 hover:text-white hover:bg-white/5 text-sm transition-colors border border-transparent hover:border-white/10"
                >
                  <Star className="w-4 h-4 text-[#e5a93c]" />
                  <span>Student Showcase</span>
                </Link>

                <Link
                  to="/gallery"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 p-2.5 text-white/80 hover:text-white hover:bg-white/5 text-sm transition-colors border border-transparent hover:border-white/10"
                >
                  <Building2 className="w-4 h-4 text-[#e5a93c]" />
                  <span>Campus Gallery</span>
                </Link>

                <Link
                  to="/contact"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 p-2.5 text-white/80 hover:text-white hover:bg-white/5 text-sm transition-colors border border-transparent hover:border-white/10"
                >
                  <Phone className="w-4 h-4 text-[#e5a93c]" />
                  <span>Contact Us</span>
                </Link>

                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 p-2.5 text-white/80 hover:text-white hover:bg-white/5 text-sm transition-colors border border-transparent hover:border-white/10"
                >
                  <LogIn className="w-4 h-4 text-[#e5a93c]" />
                  <span>Portal Login</span>
                </Link>
              </nav>
            </div>

            {/* Bottom CTA in drawer */}
            <div className="pt-6 border-t border-white/10 mt-6">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false)
                  choose('/register')
                }}
                className="w-full py-3 bg-[#e5a93c] text-black font-display font-bold text-sm uppercase tracking-wider hover:bg-[#d4a853] transition-colors shadow-lg"
              >
                Apply for Next Intake
              </button>
              {countdownLabel && (
                <p className="mt-2 text-center text-xs text-[#e5a93c] font-medium tabular-nums">
                  {countdownLabel}
                  {intake?.name ? ` · ${intake.name}` : ''}
                </p>
              )}
              <p className="mt-3 text-center text-xs text-white/40 uppercase tracking-[0.2em]">
                Kampala &bull; Uganda &bull; Est. 2015
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
